// src/pages/admin/viewHistory/helpers.js — pure formatting/merging helpers shared by
// ViewHistoryTab and its export (CSV/PDF) modules. No React state; every function is a
// function of its arguments only.

export const FUTILE_CHARGE_AMOUNT = 180;

/* ---------- linkify helper ---------- */
export const linkify = (text = "") => {
  const urlRe = /\b(https?:\/\/[^\s)]+|\bwww\.[^\s)]+)\b/gi;
  return text.replace(urlRe, (m) => {
    const url = m.startsWith("http") ? m : `https://${m}`;
    return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-blue-700 underline">${m}</a>`;
  });
};

/* ---------- address formatter ---------- */
export const addrToText = (addr) => {
  if (!addr) return "";
  if (typeof addr === "string") return addr;
  if (typeof addr === "object") {
    const {
      line1,
      line2,
      suburb,
      city,
      town,
      state,
      stateCode,
      postcode,
      postCode,
      postalCode,
      country,
      address,
      formatted,
      label,
      name,
    } = addr;
    const parts = [
      formatted || address || label || name || "",
      line1,
      line2,
      suburb || city || town,
      state || stateCode,
      postcode || postCode || postalCode,
      country,
    ].filter(Boolean);
    const seen = new Set();
    return parts
      .filter((p) => (seen.has(p) ? false : (seen.add(p), true)))
      .join(", ");
  }
  return String(addr);
};

/* ---------- time helpers ---------- */
export function parseTimeToMinutes(t) {
  if (!t || typeof t !== "string") return null;
  const s = t.trim().toLowerCase();
  const m = s.match(/(\d{1,2}):(\d{2})\s*(am|pm)?/i);
  if (!m) return null;
  let hh = parseInt(m[1], 10);
  const mm = parseInt(m[2], 10);
  const suffix = (m[3] || "").toLowerCase();
  const isPM = suffix === "pm";
  const isAM = suffix === "am";
  if (isPM && hh < 12) hh += 12;
  if (isAM && hh === 12) hh = 0;
  if (hh >= 0 && hh < 24 && mm >= 0 && mm < 60) return hh * 60 + mm;
  return null;
}
export function durationFromTrip(startTripAt, finishedAt) {
  const a = parseTimeToMinutes(startTripAt);
  const b = parseTimeToMinutes(finishedAt);
  if (a == null || b == null) return "";
  let diff = b - a;
  if (diff < 0) diff += 24 * 60;
  return `${diff} mins`;
}

export const hasValue = (v) => v !== undefined && v !== null && String(v).trim() !== "";
export const firstDefined = (...values) => values.find((v) => hasValue(v));
export const toNumber = (v) => {
  if (typeof v === "number") return Number.isFinite(v) ? v : 0;
  if (typeof v === "string") {
    const cleaned = v.replace(/[^\d.-]/g, "").trim();
    if (!cleaned) return 0;
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
};
export const fmtCurrency = (v) => {
  const n = toNumber(v);
  if (!Number.isFinite(n) || n === 0) return "";
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
};
export const looksLikeTimeOnly = (value) =>
  typeof value === "string" &&
  /^\s*\d{1,2}:\d{2}(?::\d{2})?\s*(am|pm)?\s*$/i.test(value);

export const formatExportDate = (value, fallbackDate = "") => {
  if (looksLikeTimeOnly(value)) {
    return formatExportDate(fallbackDate);
  }
  const d = toDate(value);
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) {
    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value.trim())) {
      const [y, m, day] = value.trim().split("-");
      return `${day}/${m}/${y}`;
    }
    return typeof value === "string" && !looksLikeTimeOnly(value) ? value : "";
  }
  return d.toLocaleDateString("en-AU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone: "Australia/Melbourne",
  });
};
export const csvEscape = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;

export const driverLabel = (driver = {}) =>
  driver.email || driver.displayName || driver.name || driver.uid || "Driver";

export const getRunVehicleFields = (assignment, runKey, list = []) => {
  const firstJob =
    Array.isArray(list)
      ? list.find((item) => item && !item.isWeighbridgeDocket) ||
        list.find(Boolean) ||
        null
      : null;
  const meta = assignment?.runMeta?.[runKey] || {};
  const pick = (...values) =>
    values.find((value) => value !== undefined && value !== null && String(value).trim() !== "") || "";
  const vehicleRego = pick(
    meta.vehicleRego,
    meta.rego,
    meta.truckRego,
    firstJob?.vehicleRego,
    firstJob?.rego,
    firstJob?.truckRego
  );
  const vehicleLoadType = pick(
    meta.vehicleLoadType,
    meta.loadType,
    meta.unitType,
    meta.truckType,
    firstJob?.vehicleLoadType,
    firstJob?.loadType,
    firstJob?.unitType,
    firstJob?.truckType
  );
  const vehicleLabel = pick(
    meta.vehicleLabel,
    meta.truckLabel,
    firstJob?.vehicleLabel,
    firstJob?.truckLabel,
    [vehicleRego, vehicleLoadType].filter(Boolean).join(" • "),
    vehicleRego
  );

  return { vehicleRego, vehicleLoadType, vehicleLabel };
};

/* ---------- time normalization & picking (for Pre-Start) ---------- */
export const toDate = (v) => {
  if (!v) return null;
  if (typeof v === "object" && typeof v.seconds === "number") {
    return new Date(v.seconds * 1000 + Math.floor((v.nanoseconds || 0) / 1e6));
  }
  if (v instanceof Date) return v;
  if (typeof v === "number") return new Date(v);
  if (typeof v === "string") {
    const iso = Date.parse(v);
    if (!Number.isNaN(iso)) return new Date(iso);
    return v;
  }
  return null;
};

export const fmtLocal = (v) => {
  const d = toDate(v);
  if (!d) return typeof v === "string" ? v : "";
  if (typeof v === "string") return v;
  const dd = d.toLocaleDateString("en-AU", {
    day: "2-digit",
    month: "2-digit",
    timeZone: "Australia/Melbourne",
  });
  const tt = d
    .toLocaleTimeString("en-AU", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      timeZone: "Australia/Melbourne",
    })
    .toLowerCase();
  return `${dd}, ${tt}`;
};

export const pickPreStartStart = (p) =>
  fmtLocal(
    p?.createdAt ??
      p?.openedAt ??
      p?.startedAt ??
      p?.startAt ??
      p?.started ??
      p?.startTime ??
      p?.startedTime ??
      null
  );

export const pickPreStartEnd = (p) =>
  fmtLocal(
    p?.completedAt ??
      p?.completedTime ??
      p?.finishedAt ??
      p?.submittedAt ??
      p?.updatedAt ??
      null
  );

/* ---------- SPA job link builder for <Link> ---------- */
export const buildJobHref = (location, jobId, { runKey, date, driverId }) => {
  const search = new URLSearchParams({
    runKey: runKey || "",
    date: date || "",
    driverId: driverId || "",
  }).toString();

  const baseHref = document.querySelector("base")?.getAttribute("href") || "";
  const hasAdminInBase = baseHref.includes("/admin");
  const hasAdminInPath = location.pathname.startsWith("/admin");
  const needsAdmin = hasAdminInBase || hasAdminInPath;

  return `${needsAdmin ? "/admin" : ""}/job/${jobId}?${search}`;
};

/* ---------- assignment merge helpers ---------- */
export const getRunSortNumber = (label = "") => {
  const m = String(label || "").match(/(\d+)/);
  return m ? parseInt(m[1], 10) : 999999;
};

export const mergeAssignments = (docs = []) => {
  const validDocs = docs.filter(Boolean);
  if (!validDocs.length) return null;

  const sortedDocs = [...validDocs].sort((a, b) => {
    const aTs = a?.updatedAt?.seconds
      ? a.updatedAt.seconds * 1000
      : typeof a?.updatedAt === "number"
        ? a.updatedAt
        : 0;
    const bTs = b?.updatedAt?.seconds
      ? b.updatedAt.seconds * 1000
      : typeof b?.updatedAt === "number"
        ? b.updatedAt
        : 0;
    return aTs - bTs;
  });

  const base = { ...sortedDocs[0] };
  const mergedRuns = {};
  const mergedRunMeta = {};
  const seenRunKeys = new Set();

  sortedDocs.forEach((data) => {
    Object.entries(data?.runMeta || {}).forEach(([runKey, meta]) => {
      mergedRunMeta[runKey] = {
        ...(mergedRunMeta[runKey] || {}),
        ...(meta || {}),
      };
    });

    Object.entries(data?.runs || {}).forEach(([runKey, list]) => {
      seenRunKeys.add(runKey);
      mergedRuns[runKey] = Array.isArray(list) ? list : [];
    });
  });

  const orderedRunKeys = Array.from(seenRunKeys).sort(
    (a, b) => getRunSortNumber(a) - getRunSortNumber(b)
  );

  const orderedRuns = {};
  const orderedRunMeta = {};
  orderedRunKeys.forEach((runKey) => {
    orderedRuns[runKey] = mergedRuns[runKey] || [];
    if (mergedRunMeta[runKey]) {
      orderedRunMeta[runKey] = mergedRunMeta[runKey];
    }
  });

  const finalDoc = {
    ...base,
    ...sortedDocs.reduce((acc, item) => ({ ...acc, ...item }), {}),
    runs: orderedRuns,
    runMeta: orderedRunMeta,
  };

  if (!Array.isArray(finalDoc.breaks)) {
    const breaksSource = sortedDocs.find(
      (item) => Array.isArray(item?.breaks) && item.breaks.length
    );
    if (breaksSource?.breaks) finalDoc.breaks = breaksSource.breaks;
  }

  return finalDoc;
};
